<i18n>
    [
        "global__viewAll",
        "product__prepurchaseItemsSection--title",
    ]
</i18n>

<template>
    <div class="c-pre-purchase-tray">
        <div class="c-pre-purchase-tray__header">
            <div class="c-pre-purchase-tray__header-text">
                <BaseIcon
                    icon="account--promo"
                    size="16px"
                    color="black"
                    class="c-pre-purchase-tray__promo-icon"
                />
                <span class="o-text--caption">
                    {{ t('product__prepurchaseItemsSection--title') }}
                </span>
            </div>
            <BaseLink
                v-if="showTrayHeaderLink"
                :to="trayHeaderLink.route"
                class="o-text--link o-text--caption"
            >
                {{ trayHeaderLink.linkTitle }}
            </BaseLink>
        </div>
        <Carousel
            class="c-pre-purchase-tray__carousel"
            :carouselItems="sortedChoices"
            :name="'TEMP-pre-purchase-tray'"
            :carouselItemUniqueKey="carouselItemUniqueKey"
            :desktopSlideSize="4"
            :mediumSlideSize="4"
            :mobileSlideSize="2"
            :mobilePeekPercent="50"
            :mediumPeekPercent="0"
            :noContainerMargin="true"
            :translucentButtons="false"
            :lightButtons="false"
            :catalogResponseId="null"
            :resetOnResize="true"
            :analyticsType="null"
        >
            <template
                v-for="(choice, index) in sortedChoices"
                :key="getCarouselSlotName(choice, carouselItemUniqueKey, index)"
                #[getCarouselSlotName(choice,carouselItemUniqueKey,index)]
            >
                <LazyLoad v-slot="{ hasEnteredViewport }">
                    <ChoiceTile
                        :hasEnteredViewport="hasEnteredViewport"
                        :choice="choice"
                        :displayName="productDetails.displayName"
                        :productSlug="productDetails.slug"
                        :imagePreset="imagePreset"
                        :isPrePurchase="true"
                        class="c-pre-purchase-tray__tile"
                    />
                </LazyLoad>
            </template>
        </Carousel>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { GLOBAL_IMAGE_PRESETS } from '~coreModules/core/js/constants';
import { getCarouselSlotName } from '~coreModules/core/js/carousel-utils';

import { PRODUCT_PAGE_VIEW_MODES } from '~routes/product/js/product-constants';
import { rentalRouteNames } from '~modules/core/js/router-constants';

import LazyLoad from '~coreModules/core/components/ui/LazyLoad.vue';
import Carousel from '~coreModules/core/components/ui/Carousel.vue';

import ChoiceTile from '~routes/product/components/ChoiceTile.vue';

const props = defineProps({
    productDetails: {
        type: Object,
        required: true,
    },
    selectedChoice: {
        type: Object,
        required: true,
    },
    prePurchaseEligibleChoices: {
        type: Array,
        default: () => [],
    },
});

const { t } = useI18n();

const carouselItemUniqueKey = 'choiceId';
const imagePreset = GLOBAL_IMAGE_PRESETS.product.small;
const initialSelectedChoice = ref(null);
const sortedChoices = computed(() => {
    const choices = props.prePurchaseEligibleChoices;
    const targetIndex = choices.findIndex(choice => choice.choiceId === initialSelectedChoice.value?.choiceId);

    if (targetIndex > 0) {
        const targetObject = choices.splice(targetIndex, 1)[0];
        choices.unshift(targetObject);
    }

    return choices.slice(0, 4);
});

const trayHeaderLink = computed(() => ({
    route: {
        name: rentalRouteNames.product,
        params: {
            slug: props.productDetails?.slug,
        },
        query: {
            color: initialSelectedChoice?.value?.color?.code,
            view: PRODUCT_PAGE_VIEW_MODES.BUY,
        },
    },
    linkTitle: t('global__viewAll'),
}));

const showTrayHeaderLink = computed(() => props.prePurchaseEligibleChoices?.length > 4);

onMounted(async () => {
    initialSelectedChoice.value = props.selectedChoice;
});

</script>

<style lang="scss">
    .c-pre-purchase-tray {
        &__header {
            padding: 0 $nu-spacer-2;
            display: flex;
            justify-content: space-between;
            margin-bottom: $nu-spacer-2;

            @include breakpoint(medium) {
                padding: 0;
            }
        }

        &__promo-icon {
            margin-right: $nu-spacer-1;
            vertical-align: middle;
        }
    }
</style>
